import useDriverFaceCluster from '@cobliteam/hooks/src/useCamera/useDriverFaceCluster';
import { useTranslation } from '@cobliteam/i18n';
import Avatar, { AvatarFallback, AvatarImage } from '@cobliteam/ui/components/Avatar';
import Button from '@cobliteam/ui/components/Button';
import Divider from '@cobliteam/ui/components/Divider';
import Tooltip from '@cobliteam/ui/components/Tooltip';
import Typography from '@cobliteam/ui/components/Typography';
import Icon from '@cobliteam/ui/icons';
import { cn } from '@cobliteam/ui/utils';

interface DriverFaceIdActiveCardProps {
  driverId: string;
  onManageClick: () => void;
  disabled?: boolean;
}

const MAX_STACK = 5;

export const DriverFaceIdActiveCard = ({ driverId, onManageClick, disabled }: DriverFaceIdActiveCardProps) => {
  const { t } = useTranslation('drivers');
  const { getDriverFaceCluster } = useDriverFaceCluster();
  const { data: faceId } = getDriverFaceCluster(driverId, { enabled: true });

  const urls = faceId?.sampleImageUrls?.flatMap((s) => (s ? [s.imageUrl] : [])) ?? [];
  const visible = urls.slice(0, MAX_STACK);
  const overflow = Math.max(0, urls.length - MAX_STACK);
  const totalCount = urls.length;

  const manageLabel = totalCount > 0 ? t('face_id.drawer.active.manage_with_count', { count: totalCount }) : t('face_id.drawer.active.manage');

  return (
    <div className='rounded-lg border border-gray-6 p-5'>
      <div className='flex items-center gap-1'>
        <Typography variant='text-lg-semibold' as='h3'>
          {t('face_id.drawer.active.title')}
        </Typography>
        <Tooltip content={t('face_id.drawer.active.info')}>
          <span className='text-gray-10'>
            <Icon name='info' size='sm' />
          </span>
        </Tooltip>
      </div>
      <Divider className='my-3' />
      <div className='flex items-center justify-between gap-2'>
        <div className='flex items-center justify-center rounded-full bg-gray-3 p-1' aria-hidden>
          {visible.map((src, i) => (
            <Avatar key={src + i} className={cn('size-8 rounded-full border-2 border-white bg-gray-4', i > 0 && '-ml-[10px]')}>
              <AvatarImage src={src} loading='lazy' className='object-cover' />
              <AvatarFallback>
                <Icon name='scan-face' size='sm' />
              </AvatarFallback>
            </Avatar>
          ))}
          {overflow > 0 && (
            <div className='-ml-[10px] z-10 flex size-8 items-center justify-center rounded-full border-2 border-primary-6 bg-primary-2 text-primary-9'>
              <Typography variant='text-sm-medium' className='text-primary-9'>
                +{overflow}
              </Typography>
            </div>
          )}
        </div>
        <div className='flex items-center gap-2'>
          <Tooltip enable={disabled} content={t('face_id.permission.missing')}>
            <Button type='button' variant='outlined' onClick={onManageClick} disabled={disabled} startIcon={<Icon name='images' size='sm' />}>
              {manageLabel}
            </Button>
          </Tooltip>
        </div>
      </div>
    </div>
  );
};

export default DriverFaceIdActiveCard;
